// @atlas/contracts — a `access-rights` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { I18nLabelSchema } from '../_shared/schemas.js';
export const AdminRoleSchema = z.object({
    key: z.string(),
    /**
     * A szerep megjelenített, KÉTNYELVŰ címkéje (az Odin `label Json?` — a `name` oszlopot az Odin eldobta).
     * Metaadat híján `null` → a UI a `key`-re (a `perm.role.{key}` i18n-tierre) esik vissza.
     */
    label: I18nLabelSchema.nullable(),
    /**
     * A KATALÓGUS-SOR TULAJDONOSA: `manifest` = KÓDBÓL kezelt (a `seed/atlas-rbac.json`-ból; a boot-sync
     * felülírja és prune-olja), `seed` = a manifest hozta létre, de a TARTALMA a felületé (a sync nem írja vissza,
     * nem törölhető — ilyenek a portál-szerepek), `console` = a felületen létrehozott (a sync nem nyúl hozzá). A felület ebből
     * tudja, melyik szerep szerkeszthető/törölhető TARTÓSAN — enélkül a felhasználó létrehozhatna egy szerepet,
     * ami a következő deploynál csendben eltűnik.
     */
    source: z.enum(['manifest', 'seed', 'console']).default('manifest'),
    permissions: z.array(z.string()),
});
/** Per-user FELÜLÍRÁS: a szerep-tier fölötti egyedi jog (`grant`) vagy a szerep által adott jog ELVÉTELE (`deny`). */
export const PermissionOverrideSchema = z.object({
    key: z.string(),
    effect: z.enum(['grant', 'deny']),
});
export const AdminMemberSchema = z.object({
    identityId: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    type: z.string(),
    /** Profilkép presigned URL (az objektum-tár `avatars/{identityId}` kulcsa, SeaweedFS) — null, ha nincs feltöltött kép. */
    avatarUrl: z.string().nullable(),
    /** Csapat-szín token (az avatar accentColor-ja — mint a Munkatársak-listán); null, ha nincs csapat. */
    teamColor: z.string().nullable(),
    roles: z.array(z.string()),
    /** GRANT-hatályú per-user kulcsok (visszafelé kompat — a „mit tehet extra" nézet). */
    permissions: z.array(z.string()),
    /** A teljes per-user felülírás-lista (grant + deny) — az ÚJ tier-modell override-szerkesztőjének forrása. */
    permissionOverrides: z.array(PermissionOverrideSchema),
});
/** Egy regisztrált Odin-app a KÖZPONTI (multi-app) admin-konzol app-választójához (`GET /v1/admin/apps`). */
export const AdminAppSchema = z.object({
    key: z.string(),
    name: z.string(),
    /** App-típus: `consumer` (spoke) · `platform` (Odin-governance) · `sample` (dev/teszt — a választó nem listázza). */
    kind: z.string(),
    /** Az app governance-namespace-ei (amelyekre az app-admin `{ns}:admin` jog vonatkozik). */
    namespaces: z.array(z.string()),
    status: z.string(),
    /** Tag-szám (best-effort; az Odin app-listája adja). */
    memberCount: z.number().nullable(),
});
/** Az admin-végpontok opcionális app-scope-ja: üres → a saját app (atlas); egyébként a kiválasztott app kulcsa. */
export const AdminAppQuerySchema = z.object({ app: z.string().optional() });
/**
 * RBAC-audience (felület-elkülönítés): `internal` = a belső rendszer (munkatársak), `partner` = a partner-felület
 * (külsős felhasználók). Egy app SZEREPEI és JOGAI audience szerint szétválnak — a KÖZPONTI konzol felület-váltója
 * ezt a dimenziót szűri (a member-listát a `type`-ra, a szerep/jog-katalógust az Odin `?audience=`-re). Az Odin oldalon
 * az audience az `identity.type`-hoz kötött (partner→partner; internal/service→internal), az assignment-guard a fal.
 */
export const RbacAudienceSchema = z.enum(['internal', 'partner']);
/** Roles/permissions lekérés app-scope + opcionális audience-szűrővel (a konzol felület-váltója). */
export const AdminScopeQuerySchema = AdminAppQuerySchema.extend({
    audience: RbacAudienceSchema.optional(),
});
/** A GLOBÁLIS identity-katalógus opcionális audience-szűrője (a „Tag hozzáadása" választó felajánlott poolja). */
export const AdminIdentitiesQuerySchema = z.object({ audience: RbacAudienceSchema.optional() });
/**
 * Egy jog GAZDAG metaadata a KÖZPONTI (multi-app) admin-konzol app-agnosztikus, lokalizált címkézéséhez — az
 * Odinból (minden app a SAJÁT metaadatát adja). `label`/`group` lehet null (metaadat nélküli jognál → fallback a kulcs).
 */
export const AdminPermissionSchema = z.object({
    key: z.string(),
    label: I18nLabelSchema.nullable(),
    group: z.object({ key: z.string(), hu: z.string(), en: z.string() }).nullable(),
    position: z.number(),
    /**
     * VÉDETT JOG (`permissions.protected` az Odinban): fokozott érzékenységű jog, amire három szabály él —
     * magadnak soha · csak birtokostól · szerephez SOHA nem köthető. A konzol ebből vezeti le a felületet: a
     * SZEREP-szerkesztőben a védett jog meg sem jelenik (a szerep-kötés amúgy is 422 lenne), a per-user
     * mátrixban pedig letiltott a checkbox, ha a néző nem birtokolja vagy épp önmagát szerkeszti.
     * A `.default(false)` a NON-BREAKING olvasás: egy régebbi Odin válaszában még nincs benne a mező.
     */
    protected: z.boolean().default(false),
});
/**
 * Egy belső identity a KÖZPONTI konzol „➕ Tag hozzáadása apphoz" választójához — a GLOBÁLIS stáb-katalógusból
 * (app-tagságtól függetlenül; az Odin `GET /v1/admin/identities?type=internal` proxyja). A választó ebből ajánl.
 */
export const AdminIdentitySchema = z.object({
    identityId: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
});
/**
 * „Tag hozzáadása apphoz": KIZÁRÓLAG MÁR LÉTEZŐ globális személy (`identityId`) — plusz opcionális KEZDŐ
 * szerep (`roleKey`) az app szerep-készletéből (a belső tier: member/admin/superadmin). Az Odin
 * `POST /v1/admin/apps/{app}/members` (+ `.../roles`) a fal.
 *
 * ÚJ SZEMÉLYT EZ AZ ÚT NEM HOZ LÉTRE — a provisioning-mezők (`email` + `provision` + név-részek) kivezetve; az
 * indoklás a `identityId` property kommentjében. (Ez a blokk korábban még a régi, provisioninges alakot írta le,
 * miközben a séma alatta már a szűkítettet — doksi-audit lelete.)
 */
export const AddMemberBodySchema = z.object({
    /**
     * A hozzáadandó, MÁR LÉTEZŐ globális személy azonosítója — KÖTELEZŐ.
     *
     * A „Tag hozzáadása" ÚJ SZEMÉLYT SEM HOZ LÉTRE (user-döntés): a provisioning-mezők (`email` +
     * `provision` + név-részek) KIVEZETVE. Indok: ez a felület nem tudja, KI a felvett személy — belső munkatárs
     * vagy külsős kapcsolattartó —, holott a kettő MÁS adat-vertikum (munkaviszony-adat vs. ügyfél-kötés) és MÁS
     * felület kezeli (Munkatársak, illetve Kapcsolattartók). Egy „félig felvett" személy (identity app-tagsággal,
     * de HR-/ügyfél-adat nélkül) mindkét listából kimaradt volna. A jogosultság-konzol dolga tehát KIZÁRÓLAG a
     * hozzáférés-adás egy MÁR létező személynek; a szűkítés a SZERVEREN is áll (nem csak a UI-ban elrejtve).
     */
    identityId: z.string().uuid(),
    /** Opcionális kezdő szerep az app szerep-készletéből (a kiválasztott FELÜLET — internal/partner — szerepei). */
    roleKey: z.string().max(64).optional(),
});
/**
 * ÚJ (üres jogú) szerep létrehozása a kiválasztott appban (R1 tier-modell — főleg egy új app tier-bootstrapjához;
 * a jogokat utána a szerep-szerkesztő állítja be). Kulcs = slug (`[a-z0-9_-]`), az Odin `POST .../roles`-a a fal.
 */
export const CreateRoleBodySchema = z.object({
    key: z
        .string()
        .trim()
        .regex(/^[a-z0-9_-]+$/)
        .max(64),
    /** A szerep KÉTNYELVŰ címkéje (az Odin `upsertRole` `label {hu,en}`-jét várja; a `name` oszlop megszűnt). */
    label: I18nLabelSchema,
    /**
     * A szerep AUDIENCE-e (`internal` | `partner`) — melyik felülethez tartozik. Hiányában az Odin `internal`-t
     * feltételez (visszafelé kompat). A konzol a kiválasztott felület-váltó szerint küldi.
     */
    audience: RbacAudienceSchema.optional(),
});
//# sourceMappingURL=schemas.js.map